import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  AlertCircle,
  ArrowRight,
  Clock3,
  Loader2,
  MessageSquareText,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { ConsentText } from "@/components/consent-text";
import { TrustBadge, TrustBadgeRow } from "@/components/trust-badge";
import { Button } from "@/components/ui/button";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { Label } from "@/components/ui/label";
import { PhoneInput } from "@/components/ui/phone-input";
import {
  type ConsentAcceptance,
  consentGrants,
  readAcceptance,
} from "@/lib/consent";
import { sendCode, verifyCode } from "@/lib/data";
import { getReferralIdentifiers } from "@/lib/referral";
import { redirectSignedInApplicant, startSession } from "@/lib/session";
import { useOrganization } from "@/lib/use-organization";

const RESEND_COOLDOWN_SECONDS = 30;

export const Route = createFileRoute("/")({
  component: LoginPage,
  validateSearch: (
    search: Record<string, unknown>,
  ): { phone?: string; pending?: boolean } => {
    const phone = typeof search.phone === "string" ? search.phone : undefined;
    const pending =
      search.pending === "1" || search.pending === 1 ? true : undefined;
    return {
      ...(phone ? { phone } : {}),
      ...(pending ? { pending } : {}),
    };
  },
  beforeLoad: redirectSignedInApplicant,
});

function LoginPage() {
  const organization = useOrganization();
  const navigate = useNavigate();
  const { phone: initialPhone, pending } = Route.useSearch();
  const codeAlreadySent = pending === true && Boolean(initialPhone);
  const [step, setStep] = useState<"phone" | "verify">(
    codeAlreadySent ? "verify" : "phone",
  );
  const [phoneNumber, setPhoneNumber] = useState(initialPhone ?? "");
  const [verificationCode, setVerificationCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(
    codeAlreadySent ? RESEND_COOLDOWN_SECONDS : 0,
  );
  // The notice under the phone step's continue button. What it said is kept when they continue
  // and sent once the code checks out; a dealer's deep link lands past it, so sends none.
  const noticeRef = useRef<HTMLSpanElement>(null);
  const [agreement, setAgreement] = useState<ConsentAcceptance | null>(null);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const id = setInterval(
      () => setResendCooldown((s) => Math.max(0, s - 1)),
      1000,
    );
    return () => clearInterval(id);
  }, [resendCooldown]);

  async function handleSendCode(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await sendCode(phoneNumber);
      if (noticeRef.current) {
        setAgreement(
          readAcceptance(noticeRef.current, consentGrants(organization)),
        );
      }
      setStep("verify");
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
    } catch {
      setError(
        "Unable to send verification code. Please check your phone number.",
      );
    } finally {
      setLoading(false);
    }
  }

  async function handleResendCode(channel: "sms" | "call") {
    if (resendCooldown > 0) return;
    setLoading(true);
    setError(null);

    try {
      await sendCode(phoneNumber, channel);
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
    } catch {
      setError("Unable to resend verification code.");
    } finally {
      setLoading(false);
    }
  }

  async function handleVerifyCode(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await verifyCode(
        phoneNumber,
        verificationCode,
        getReferralIdentifiers(),
        agreement ? [agreement] : undefined,
      );
      void navigate(await startSession(response.token));
    } catch {
      setError("Invalid verification code. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-background">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-[60vh] bg-[radial-gradient(ellipse_at_top,var(--brand-tint),transparent_70%)]"
      />

      <div className="relative mx-auto flex min-h-screen w-full max-w-md flex-col px-5 pt-10 pb-10 sm:pt-16">
        <header className="flex flex-col items-center text-center">
          {organization.logo_url ? (
            <img
              src={organization.logo_url}
              alt={organization.name}
              className="mb-5 h-14 w-auto"
            />
          ) : (
            <p className="mb-5 text-xs font-medium tracking-wider text-primary uppercase">
              {organization.name}
            </p>
          )}
          <h1 className="font-heading text-[2rem] leading-tight font-semibold text-foreground">
            {step === "phone" ? "Sign up / Sign in" : "Verify your phone"}
          </h1>
          <p className="mt-2 max-w-xs text-[0.95rem] leading-relaxed text-muted-foreground">
            {step === "phone"
              ? "Start fresh or pick up where you left off."
              : "Enter the 6-digit code we just sent to your phone."}
          </p>
        </header>

        {step === "phone" && organization.landing_page_content && (
          <div
            className="prose prose-sm mt-6 rounded-xl border border-border bg-card p-4 text-muted-foreground shadow-[var(--shadow-card)]"
            // eslint-disable-next-line @eslint-react/dom-no-dangerously-set-innerhtml -- Trusted content from organization settings
            dangerouslySetInnerHTML={{
              __html: organization.landing_page_content,
            }}
          />
        )}

        <div className="mt-8 rounded-2xl border border-border bg-card p-5 shadow-[var(--shadow-elevated)] sm:p-6">
          {error && (
            <div className="mb-5 flex animate-in items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/8 p-3 text-sm text-destructive fade-in slide-in-from-top-1">
              <AlertCircle className="mt-0.5 size-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {step === "phone" ? (
            <form onSubmit={handleSendCode} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-sm font-medium">
                  Phone number
                </Label>
                <PhoneInput
                  id="phone"
                  placeholder="555-123-4567"
                  value={phoneNumber}
                  onChange={setPhoneNumber}
                  required
                  autoFocus
                />
              </div>
              <Button
                type="submit"
                className="w-full"
                size="lg"
                data-icon="inline-end"
                disabled={loading || !phoneNumber}
              >
                {loading ? (
                  <>
                    <Loader2 className="size-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    Continue
                    <ArrowRight className="size-4" />
                  </>
                )}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleVerifyCode} className="space-y-5">
              <div className="flex justify-center">
                <InputOTP
                  maxLength={6}
                  value={verificationCode}
                  onChange={setVerificationCode}
                  autoFocus
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>
              </div>
              <Button
                type="submit"
                className="w-full"
                size="lg"
                disabled={loading || verificationCode.length < 6}
              >
                {loading ? (
                  <>
                    <Loader2 className="size-4 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  "Verify code"
                )}
              </Button>

              <div className="flex flex-col items-center gap-1 pt-2 text-center text-sm">
                <p className="text-muted-foreground">
                  Code sent to{" "}
                  <strong className="font-semibold text-foreground">
                    {phoneNumber}
                  </strong>
                </p>
                <button
                  type="button"
                  className="text-primary underline-offset-4 hover:underline"
                  onClick={() => {
                    setStep("phone");
                    setVerificationCode("");
                    setError(null);
                  }}
                >
                  Use a different number
                </button>
              </div>

              <div className="flex flex-col gap-2 border-t border-border pt-4">
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  size="lg"
                  disabled={loading || resendCooldown > 0}
                  onClick={() => handleResendCode("sms")}
                >
                  {resendCooldown > 0
                    ? `Resend code in ${resendCooldown}s`
                    : "Resend code via SMS"}
                </Button>
                <button
                  type="button"
                  className="text-center text-xs text-muted-foreground hover:text-foreground disabled:opacity-50"
                  disabled={loading || resendCooldown > 0}
                  onClick={() => handleResendCode("call")}
                >
                  Get code via phone call
                </button>
              </div>
            </form>
          )}
        </div>

        {step === "phone" && (
          <TrustBadgeRow className="mt-6">
            <TrustBadge icon={<MessageSquareText />}>SMS secure</TrustBadge>
            <TrustBadge icon={<Clock3 />}>2 minutes to start</TrustBadge>
          </TrustBadgeRow>
        )}

        {step === "phone" && (
          <p className="mt-6 text-center text-xs leading-relaxed text-muted-foreground">
            <ConsentText agreedBy="clicking continue" ref={noticeRef} />
          </p>
        )}
      </div>
    </div>
  );
}
